import React from "react";
import { Form, InputGroup, Tab, Tabs } from "react-bootstrap";
import InputGroupText from "react-bootstrap/esm/InputGroupText";
import Input from "../Builder/nodes/Logic/Inputs/Input";
import SelectApiConnection from "../Monitoring/SelectApiConnection";
import ApiConnection from "../Common/DataImportModal/ApiConnection";
import SolutionImportCheckContactField from "./SolutionImportCheckContactField";
import SolutionImportCheckReportingTemplate from "./SolutionImportCheckReportingTemplate";
import { toast } from "react-toastify";
import MyCollapse from "../Common/MyCollapse";

function ImportFieldInfo({ info }: { info: any }) {
  if (
    !info ||
    info.length === 0 ||
    !info.some((i: any) => i.Help && i.Help.length > 0)
  ) {
    return null;
  }

  return (
    <div className="mt-2">
      {info?.map((comp: any, index: number) => (
        <div key={index}>
          <MyCollapse
            heading={"This field is used in " + comp.Help?.length + " places"}
          >
            {comp.Help?.map((line: any, lineIndex: number) => (
              <div key={lineIndex}>
                <span className="fw-bold">{comp.SolutionComponentLabel}</span>{" "}
                component in {line.InComponentKind}{" "}
                {line.ActionKind && (
                  <span className="fw-bold">{line.ActionKind}</span>
                )}{" "}
                in{" "}
                {line.WorkflowLabel && (
                  <span className="fw-bold">{line.WorkflowLabel}</span>
                )}{" "}
                when{" "}
                {line.EventType && (
                  <>
                    Event: <span className="fw-bold">{line.EventType}</span>
                  </>
                )}
                {line.MessageLabel && (
                  <>
                    Message:
                    <span className="fw-bold"> ${line.MessageLabel}</span>
                  </>
                )}
              </div>
            ))}
          </MyCollapse>
        </div>
      ))}
    </div>
  );
}

type Props = {
  fieldType: string;
  fieldKey: string;
  value?: any;
  importedJSON?: any;
  setImportedJSON?: React.Dispatch<React.SetStateAction<any>>;
  manualFields?: string[];
  setManualFields?: React.Dispatch<React.SetStateAction<string[]>>;
  contactSets?: any[];
  reportingTopics?: any[];
  apiProfiles?: any;
  setApiProfiles?: React.Dispatch<React.SetStateAction<any>>;
  updateApiConnection?: (args: any) => Promise<any>;
  previewOnly?: boolean;
  previewLabel?: string;
};

export default function SolutionImportInput({
  fieldType,
  fieldKey,
  value = "",
  importedJSON,
  setImportedJSON,
  manualFields = [],
  setManualFields = () => {},
  contactSets = [],
  reportingTopics = [],
  apiProfiles = {},
  setApiProfiles = () => {},
  updateApiConnection = async () => ({} as any),
  previewOnly = false,
  previewLabel,
}: Props) {
  if (previewOnly) {
    return (
      <div className="rounded px-2 py-1 w-100 text-center btn btn-light">
        <div>{previewLabel || fieldKey}</div>
        <div className="text-secondary text-small">{fieldType}</div>
      </div>
    );
  }

  if (!importedJSON || !setImportedJSON || !importedJSON.Item) {
    return null;
  }

  const isManual = manualFields.includes(fieldKey);
  const isEmpty = value === "" || value === null || value === undefined;
  const label = (() => {
    if (fieldType === "ContactField") {
      return importedJSON.Item?.ContactFields?.find(
        (f: any) => f.uuid === fieldKey
      )?.label;
    }
    if (fieldType === "ContactSet") {
      return importedJSON.Item?.ContactGroups?.find(
        (f: any) => f.uuid === fieldKey
      )?.label;
    }
    if (fieldType === "ReportingTemplate") {
      return importedJSON.Item?.ReportingTemplates?.find(
        (f: any) => f.uuid === fieldKey
      )?.label;
    }
    if (fieldType === "Topic") {
      return importedJSON.Item?.ReportingTopics?.find(
        (f: any) => f.uuid === fieldKey
      )?.label;
    }
    if (fieldType === "ApiConnection") {
      return fieldKey;
    }
    return fieldKey;
  })();

  const setFieldValue = (nextValue: any) => {
    setImportedJSON((prev: any) => {
      const updated = { ...prev };
      if (!updated.Bag?.External?.[fieldType]) return updated;
      updated.Bag.External[fieldType][fieldKey] = nextValue;
      return updated;
    });
  };

  const disableManual = () => {
    setManualFields((prev) => prev.filter((f) => f !== fieldKey));
    setFieldValue("");
  };

  if (fieldType === "ContactField") {
    return (
      <div className="mb-3 p-3 bg-purple-gradient-darker-reverse rounded-3">
        <SolutionImportCheckContactField
          fieldkey={fieldKey}
          importedJSON={importedJSON}
          setImportedJSON={setImportedJSON}
          value={value}
          manualFields={manualFields}
          setManualFields={setManualFields}
        />
        <ImportFieldInfo
          info={
            importedJSON.Item?.ContactFields?.find(
              (f: any) => f.uuid === fieldKey
            )?.info
          }
        />
      </div>
    );
  }

  if (fieldType === "ReportingTemplate") {
    return (
      <div className="mb-3 p-3 bg-purple-gradient-darker-reverse rounded-3">
        <SolutionImportCheckReportingTemplate
          importedJSON={importedJSON}
          setImportedJSON={setImportedJSON}
          templateKey={fieldKey}
          value={value}
          manualFields={manualFields}
          setManualFields={setManualFields}
        />
        <ImportFieldInfo
          info={
            importedJSON.Item?.ReportingTemplates?.find(
              (f: any) => f.uuid === fieldKey
            )?.info
          }
        />
      </div>
    );
  }

  if (fieldType === "ContactSet") {
    return (
      <div className="mb-3 p-3 bg-purple-gradient-darker-reverse rounded-3">
        <InputGroup>
          <InputGroupText className="col-3">{label}</InputGroupText>
          {isEmpty && !isManual ? (
            <InputGroupText className="text-muted col" style={{ height: 40 }}>
              This field will be created automatically upon deployment. Or you
              can{" "}
              <button
                className="btn btn-link p-0 align-baseline ms-1"
                onClick={() => {
                  setManualFields((prev) => [...prev, fieldKey]);
                }}
              >
                manage manually
              </button>
              .
            </InputGroupText>
          ) : isManual ? (
            <div className="col ms-n1">
              <div className="mb-2">
                <Form.Check
                  inline
                  type="radio"
                  id={`${fieldKey}-contactset-select-existing`}
                  label="Select existing group"
                  name={`${fieldKey}-contactset-choice`}
                  checked={
                    value !== null &&
                    value !== undefined &&
                    !value.startsWith("NEW:")
                  }
                  onChange={() => setFieldValue("")}
                />
                <Form.Check
                  inline
                  type="radio"
                  id={`${fieldKey}-contactset-create-new`}
                  label="Create new group"
                  name={`${fieldKey}-contactset-choice`}
                  checked={
                    value === null ||
                    value === undefined ||
                    value.startsWith("NEW:")
                  }
                  onChange={() => setFieldValue("NEW:")}
                />
              </div>
              {value === "" || (value && !value.startsWith("NEW:")) ? (
                <Input
                  placeholder="Select an existing contact group"
                  inputType="select"
                  value={[value]}
                  onChange={([v]: any) => setFieldValue(v)}
                  options={contactSets?.map((cf: any) => ({
                    value: cf.uuid,
                    label: cf.label,
                  }))}
                />
              ) : (
                <Form.Control
                  type="text"
                  placeholder="Enter a name for the new contact group"
                  value={value?.startsWith("NEW:") ? value.substring(4) : ""}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    const trimmedValue = newValue.trim();
                    setFieldValue(trimmedValue ? `NEW:${newValue}` : "NEW:");
                  }}
                />
              )}
            </div>
          ) : (
            <div className="col ms-n1">
              <Input
                placeholder="Select an existing contact group"
                inputType="select"
                value={[value]}
                onChange={([v]: any) => setFieldValue(v)}
                options={contactSets?.map((cf: any) => ({
                  value: cf.uuid,
                  label: cf.label,
                }))}
              />
            </div>
          )}
          <div className="col-auto d-flex justify-content-end align-items-center">
            <Form.Check
              id={`contact-set-manual-${fieldKey}`}
              type="switch"
              label="Manage manually"
              checked={isManual}
              onChange={(e) => {
                if (e.target.checked) {
                  setManualFields((prev) => [...prev, fieldKey]);
                } else {
                  disableManual();
                }
              }}
              className="ms-3"
            />
          </div>
        </InputGroup>
        <ImportFieldInfo
          info={
            importedJSON.Item?.ContactGroups?.find(
              (f: any) => f.uuid === fieldKey
            )?.info
          }
        />
      </div>
    );
  }

  if (fieldType === "Topic") {
    return (
      <div className="mb-3 p-3 bg-purple-gradient-darker-reverse rounded-3">
        <InputGroup>
          <InputGroupText className="col-3">{label}</InputGroupText>
          {isEmpty && !isManual ? (
            <InputGroupText className="text-muted col" style={{ height: 40 }}>
              This topic will be created automatically upon deployment. Or you
              can{" "}
              <button
                className="btn btn-link p-0 align-baseline ms-1"
                onClick={() => {
                  setManualFields((prev) => [...prev, fieldKey]);
                }}
              >
                manage manually
              </button>
              .
            </InputGroupText>
          ) : isManual ? (
            <div className="col ms-n1">
              <div className="mb-2">
                <Form.Check
                  inline
                  type="radio"
                  id={`${fieldKey}-topic-select-existing`}
                  label="Select existing topic"
                  name={`${fieldKey}-topic-choice`}
                  checked={
                    value !== null &&
                    value !== undefined &&
                    !value.startsWith("NEW:")
                  }
                  onChange={() => setFieldValue("")}
                />
                <Form.Check
                  inline
                  type="radio"
                  id={`${fieldKey}-topic-create-new`}
                  label="Create new topic"
                  name={`${fieldKey}-topic-choice`}
                  checked={
                    value === null ||
                    value === undefined ||
                    value.startsWith("NEW:")
                  }
                  onChange={() => setFieldValue("NEW:")}
                />
              </div>
              {value === "" || (value && !value.startsWith("NEW:")) ? (
                <Input
                  placeholder="Select an existing topic"
                  inputType="select"
                  value={[value]}
                  onChange={([v]: any) => setFieldValue(v)}
                  options={reportingTopics?.map((cf: any) => ({
                    value: cf.uuid,
                    label: cf.label,
                  }))}
                />
              ) : (
                <Form.Control
                  type="text"
                  placeholder="Enter a name for the new topic"
                  value={value?.startsWith("NEW:") ? value.substring(4) : ""}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    const trimmedValue = newValue.trim();
                    setFieldValue(trimmedValue ? `NEW:${newValue}` : "NEW:");
                  }}
                />
              )}
            </div>
          ) : (
            <div className="col ms-n1">
              <Input
                placeholder="Select an existing topic"
                inputType="select"
                value={[value]}
                onChange={([v]: any) => setFieldValue(v)}
                options={reportingTopics?.map((cf: any) => ({
                  value: cf.uuid,
                  label: cf.label,
                }))}
              />
            </div>
          )}
          <div className="col-auto d-flex justify-content-end align-items-center">
            <Form.Check
              id={`reporting-topic-manual-${fieldKey}`}
              type="switch"
              label="Manage manually"
              checked={isManual}
              onChange={(e) => {
                if (e.target.checked) {
                  setManualFields((prev) => [...prev, fieldKey]);
                } else {
                  disableManual();
                }
              }}
              className="ms-3"
            />
          </div>
        </InputGroup>
        <ImportFieldInfo
          info={
            importedJSON.Item?.ReportingTopics?.find(
              (f: any) => f.uuid === fieldKey
            )?.info
          }
        />
      </div>
    );
  }

  if (fieldType === "ApiConnection") {
    return (
      <div className="mb-3 p-3 bg-purple-gradient-darker-reverse rounded-3">
        <div className="d-flex justify-content-end mb-2">
          <Form.Check
            id={`api-connection-manual-${fieldKey}`}
            type="switch"
            label="Manage manually"
            checked={isManual}
            onChange={(e) => {
              if (e.target.checked) {
                setManualFields((prev) => [...prev, fieldKey]);
              } else {
                disableManual();
              }
            }}
            className="ms-3"
          />
        </div>
        {!value && !isManual ? (
          <InputGroupText className="text-muted">
            This API Connection will be created automatically upon deployment.
            Or you can{" "}
            <button
              className="btn btn-link p-0 align-baseline ms-1"
              onClick={() => {
                setManualFields((prev) => [...prev, fieldKey]);
              }}
            >
              manage manually
            </button>
            .
          </InputGroupText>
        ) : (
          <div>
            Please select an existing API Connection or create a new one:
            <Tabs defaultActiveKey="select" className="mb-3">
              <Tab eventKey="select" title="Select Existing">
                <h5>Select an API Connection</h5>
                <SelectApiConnection
                  selected={value}
                  onSelect={(api) => {
                    setFieldValue(api?.Uuid);
                  }}
                />
              </Tab>
              <Tab eventKey="create" title="Create New">
                <h5>Create a New API Connection</h5>
                <ApiConnection
                  api={apiProfiles[fieldKey] || {}}
                  onChange={(Profile) => {
                    setApiProfiles((prev: any) => ({
                      ...prev,
                      [fieldKey]: Profile,
                    }));
                  }}
                />
                <button
                  className="btn btn-primary mt-3"
                  disabled={!apiProfiles[fieldKey]}
                  onClick={async () => {
                    const res: any = await updateApiConnection({
                      Status: "production",
                      Kind: "third-party",
                      Label: fieldKey + " API Connection",
                      Profile: apiProfiles[fieldKey],
                    });
                    if (res?.data?.Uuid) {
                      toast.success("API Connection created");
                      setFieldValue(res.data.Uuid);
                      setApiProfiles((prev: any) => ({
                        ...prev,
                        [fieldKey]: null,
                      }));
                    }
                  }}
                >
                  Create API Connection
                </button>
              </Tab>
            </Tabs>
          </div>
        )}
      </div>
    );
  }

  return null;
}
